import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'

import NavBar from '../components/layout/NavBar'
import BusinessAvatar from '../components/businesses/BusinessAvatar'
import BookingCalendar from '../components/booking/BookingCalendar'
import BookingDaySlots from '../components/booking/BookingDaySlots'
import NextAvailableButton from '../components/booking/NextAvailableButton'
import PeopleActionRail from '../components/PeopleActionRail'
import BusinessStory from '../components/businesses/BusinessStory'
import GiftDrawer from '../components/gifts/GiftDrawer'
import GiftToast from '../components/gifts/GiftToast'
import { useGiftCatalog } from '../hooks/useGifts'
import { newIdempotencyKey } from '../lib/idempotency'
import { bookingAvailability, cancelBookingWithReason, createBooking, listBusinessPublicBookingServices, quoteBooking, rescheduleBooking } from '../api/bookings'
import {
  businessActionsSummary,
  businessMetricsSummary,
  callClick,
  createBusinessReview,
  createBusinessComment,
  followBusiness,
  likeBusiness,
  listBusinessHours,
  listBusinessOffers,
  listBusinessComments,
  listBusinessReviews,
  listPublicTeam,
  publicBusiness,
  saveBusiness,
  sendBusinessGift,
  shareBusiness,
  unfollowBusiness,
  unlikeBusiness,
  unsaveBusiness,
} from '../api/businessesReader'
import { getToken } from '../lib/auth'
import { addDaysIso, getMonthDates, monthStartIso, todayIsoDate } from '../lib/bookingCalendar'
import type {
  BusinessComment,
  BusinessHourRange,
  BusinessHoursGrouped,
  BusinessOffer,
  BusinessProfile,
  BusinessReview,
  BusinessReviewSummary,
  BusinessTeamMember,
  BusinessMetricsSummary,
} from '../types/businesses'
import type { BookingQuoteResponse, BookingService, BookingSlot, BookingStaffMember } from '../types/bookings'

type SectionKey = 'about' | 'posts' | 'offers' | 'reviews' | 'team' | 'booking' | 'contact' | 'hours' | 'comments'

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const stripePublicKey = (import.meta as any).env?.VITE_STRIPE_PUBLISHABLE_KEY || ''
const stripePromise = stripePublicKey ? loadStripe(stripePublicKey) : null

function BookingPaymentConfirm({
  onSuccess,
  onError,
  busy,
  setBusy,
}: {
  onSuccess: () => void
  onError: (message: string) => void
  busy: boolean
  setBusy: (value: boolean) => void
}) {
  const stripe = useStripe()
  const elements = useElements()

  const onConfirm = async () => {
    if (!stripe || !elements || busy) return
    setBusy(true)
    onError('')
    const result = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    })
    if (result.error) {
      onError(result.error.message || 'Payment confirmation failed.')
      setBusy(false)
      return
    }
    setBusy(false)
    onSuccess()
  }

  return (
    <div className="space-y-3">
      <PaymentElement />
      <button
        type="button"
        onClick={onConfirm}
        disabled={busy || !stripe || !elements}
        className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
      >
        {busy ? 'Processing...' : 'Pay deposit'}
      </button>
    </div>
  )
}

function CopyButton({ value }: { value: string }) {
  if (!value) return null
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value)
        } catch {}
      }}
      className="rounded border border-white/15 bg-white/5 px-2 py-1 text-xs hover:bg-white/10"
    >
      Copy
    </button>
  )
}

function parseTimeToMinutes(raw: string): number {
  const parts = String(raw || '').split(':')
  const hour = Number(parts[0] || 0)
  const minute = Number(parts[1] || 0)
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return 0
  return (hour * 60) + minute
}

function formatTimeLabel(raw: string, timeZone?: string): string {
  const [h, m] = String(raw || '00:00').split(':')
  const dt = new Date(Date.UTC(2020, 0, 1, Number(h || 0), Number(m || 0), 0))
  return dt.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: timeZone || 'UTC',
  })
}

function toDateTimeLocalValue(iso: string | null | undefined): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  const hh = String(date.getHours()).padStart(2, '0')
  const mm = String(date.getMinutes()).padStart(2, '0')
  return `${y}-${m}-${d}T${hh}:${mm}`
}

function fromDateTimeLocalValue(value: string): string | null {
  const raw = String(value || '').trim()
  if (!raw) return null
  const date = new Date(raw)
  if (Number.isNaN(date.getTime())) return null
  return date.toISOString()
}

function getNowInTimezone(timeZone: string): { weekday: number; minutes: number } {
  try {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: timeZone || 'UTC',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
    const parts = fmt.formatToParts(new Date())
    const weekdayStr = parts.find((p) => p.type === 'weekday')?.value || 'Mon'
    const hour = Number(parts.find((p) => p.type === 'hour')?.value || '0')
    const minute = Number(parts.find((p) => p.type === 'minute')?.value || '0')
    const weekdayMap: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 }
    return {
      weekday: weekdayMap[weekdayStr] ?? 0,
      minutes: (hour * 60) + minute,
    }
  } catch {
    const now = new Date()
    const jsDay = now.getDay()
    const weekday = jsDay === 0 ? 6 : jsDay - 1
    return { weekday, minutes: (now.getHours() * 60) + now.getMinutes() }
  }
}

export default function BusinessReader() {
  const { slug = '' } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<BusinessProfile | null>(null)
  const [activeSection, setActiveSection] = useState<SectionKey>('about')
  const [callBusy, setCallBusy] = useState(false)
  const [callMsg, setCallMsg] = useState<string | null>(null)
  const [reviewSort, setReviewSort] = useState<'newest' | 'highest' | 'lowest'>('newest')
  const [reviewsLoading, setReviewsLoading] = useState(false)
  const [reviewsSummary, setReviewsSummary] = useState<BusinessReviewSummary | null>(null)
  const [reviews, setReviews] = useState<BusinessReview[]>([])
  const [reviewHasNext, setReviewHasNext] = useState(false)
  const [reviewPage, setReviewPage] = useState(1)
  const [reviewError, setReviewError] = useState<string | null>(null)
  const [showReviewComposer, setShowReviewComposer] = useState(false)
  const [reviewRating, setReviewRating] = useState(5)
  const [reviewBody, setReviewBody] = useState('')
  const [reviewSubmitting, setReviewSubmitting] = useState(false)
  const [teamMembers, setTeamMembers] = useState<BusinessTeamMember[]>([])
  const [teamExpanded, setTeamExpanded] = useState(false)
  const [railLiked, setRailLiked] = useState(false)
  const [railSaved, setRailSaved] = useState(false)
  const [railLikesCount, setRailLikesCount] = useState(0)
  const [railCommentsCount, setRailCommentsCount] = useState(0)
  const [railSavesCount, setRailSavesCount] = useState(0)
  const [railSharesCount, setRailSharesCount] = useState(0)
  const [railGiftCount, setRailGiftCount] = useState(0)
  const [railGiftTotal, setRailGiftTotal] = useState('0.00')
  const [giftOpen, setGiftOpen] = useState(false)
  const [giftSending, setGiftSending] = useState(false)
  const { items: giftCatalog } = useGiftCatalog()
  const [toast, setToast] = useState<{ visible: boolean; kind?: 'success' | 'error'; emoji: string; label: string; detail?: string }>({
    visible: false,
    kind: 'success',
    emoji: '🎁',
    label: '',
    detail: '',
  })
  const [commentsLoading, setCommentsLoading] = useState(false)
  const [commentsError, setCommentsError] = useState<string | null>(null)
  const [comments, setComments] = useState<BusinessComment[]>([])
  const [commentsHasNext, setCommentsHasNext] = useState(false)
  const [commentsPage, setCommentsPage] = useState(1)
  const [commentBody, setCommentBody] = useState('')
  const [commentPosting, setCommentPosting] = useState(false)
  const [hoursLoading, setHoursLoading] = useState(false)
  const [hoursError, setHoursError] = useState<string | null>(null)
  const [hoursData, setHoursData] = useState<BusinessHoursGrouped | null>(null)
  const [offersLoading, setOffersLoading] = useState(false)
  const [offersError, setOffersError] = useState<string | null>(null)
  const [offers, setOffers] = useState<BusinessOffer[]>([])
  const [bookingDate, setBookingDate] = useState('')
  const [bookingVisibleMonth, setBookingVisibleMonth] = useState('')
  const [bookingLoading, setBookingLoading] = useState(false)
  const [bookingError, setBookingError] = useState<string | null>(null)
  const [bookingReasonCode, setBookingReasonCode] = useState<'DISABLED' | 'CLOSED_SPECIAL' | 'NO_HOURS' | 'OUTSIDE_WINDOW' | 'LEAD_TIME' | null>(null)
  const [bookingEnabled, setBookingEnabled] = useState(true)
  const [bookingClosed, setBookingClosed] = useState(false)
  const [bookingSlots, setBookingSlots] = useState<BookingSlot[]>([])
  const [bookingServices, setBookingServices] = useState<BookingService[]>([])
  const [bookingServiceId, setBookingServiceId] = useState<number | null>(null)
  const [bookingStaffMode, setBookingStaffMode] = useState<'ANY' | 'SPECIFIC'>('ANY')
  const [bookingStaffId, setBookingStaffId] = useState<number | null>(null)
  const [bookingStaffList, setBookingStaffList] = useState<BookingStaffMember[]>([])
  const [bookingDurationMinutes, setBookingDurationMinutes] = useState(30)
  const [bookingTimezone, setBookingTimezone] = useState('UTC')
  const [bookingQuote, setBookingQuote] = useState<BookingQuoteResponse | null>(null)
  const [bookingOpen, setBookingOpen] = useState(false)
  const [bookingSelected, setBookingSelected] = useState<BookingSlot | null>(null)
  const [bookingSubmitting, setBookingSubmitting] = useState(false)
  const [bookingPaymentSubmitting, setBookingPaymentSubmitting] = useState(false)
  const [bookingClientSecret, setBookingClientSecret] = useState('')
  const [bookingPaymentExpiresAt, setBookingPaymentExpiresAt] = useState<string | null>(null)
  const [bookingPaymentRemainingSeconds, setBookingPaymentRemainingSeconds] = useState(0)
  const [bookingName, setBookingName] = useState('')
  const [bookingEmail, setBookingEmail] = useState('')
  const [bookingPhone, setBookingPhone] = useState('')
  const [bookingNotes, setBookingNotes] = useState('')
  const [latestBooking, setLatestBooking] = useState<{ public_id: string; starts_at: string; ends_at: string; payment_status: 'NONE' | 'REQUIRES_PAYMENT' | 'PAID' | 'FAILED' | 'REFUNDED' } | null>(null)
  const [bookingActionBusy, setBookingActionBusy] = useState(false)
  const [bookingRescheduleOpen, setBookingRescheduleOpen] = useState(false)
  const [bookingRescheduleDateTime, setBookingRescheduleDateTime] = useState('')
  const [bookingFindingNext, setBookingFindingNext] = useState(false)
  const bookingSlotsRef = useRef<HTMLDivElement | null>(null)
  const bookingDayCacheRef = useRef<Record<string, {
    slots: BookingSlot[]
    enabled: boolean
    closed: boolean
    reasonCode: 'DISABLED' | 'CLOSED_SPECIAL' | 'NO_HOURS' | 'OUTSIDE_WINDOW' | 'LEAD_TIME' | null
    durationMinutes: number
    timezone: string
    staffMode: 'ANY' | 'SPECIFIC'
    staffList: BookingStaffMember[]
  }>>({})
  const [bookingDaySummaries, setBookingDaySummaries] = useState<Record<string, {
    availableCount: number
    enabled: boolean
    closed: boolean
    loading?: boolean
  }>>({})
  const [metrics, setMetrics] = useState<BusinessMetricsSummary | null>(null)
  const [followPending, setFollowPending] = useState(false)
  const commentInputRef = useRef<HTMLTextAreaElement | null>(null)
  const authed = Boolean(getToken())

  const applyBookingDay = useCallback((date: string, payload: {
    slots: BookingSlot[]
    enabled: boolean
    closed: boolean
    reasonCode: 'DISABLED' | 'CLOSED_SPECIAL' | 'NO_HOURS' | 'OUTSIDE_WINDOW' | 'LEAD_TIME' | null
    durationMinutes: number
    timezone: string
    staffMode: 'ANY' | 'SPECIFIC'
    staffList: BookingStaffMember[]
  }) => {
    bookingDayCacheRef.current = { ...bookingDayCacheRef.current, [date]: payload }
    setBookingDaySummaries((prev) => ({
      ...prev,
      [date]: {
        availableCount: payload.slots.length,
        enabled: payload.enabled,
        closed: payload.closed,
      },
    }))
  }, [])

  const fetchBookingDay = useCallback(
    async (
      date: string,
      options: { force?: boolean; quiet?: boolean } = {},
    ) => {
      if (!slug || !date) return null
      const cached = bookingDayCacheRef.current[date]
      if (cached && !options.force) return cached
      if (!options.quiet) {
        setBookingLoading(true)
        setBookingError(null)
      } else {
        setBookingDaySummaries((prev) => ({ ...prev, [date]: { ...(prev[date] || { availableCount: 0, enabled: true, closed: false }), loading: true } }))
      }
      try {
        const row = await bookingAvailability({
          host_type: 'business',
          slug,
          date,
          service_id: bookingServiceId ?? undefined,
          staff_mode: bookingStaffMode,
          staff_id: bookingStaffMode === 'SPECIFIC' ? (bookingStaffId ?? undefined) : undefined,
        })
        const payload = {
          slots: Array.isArray(row.slots) ? row.slots : [],
          enabled: Boolean(row.enabled),
          closed: Boolean(row.closed),
          reasonCode: row.reason_code || null,
          durationMinutes: Number(row.duration_minutes || 30),
          timezone: String(row.timezone || 'UTC'),
          staffMode: row.staff_mode === 'SPECIFIC' ? 'SPECIFIC' : 'ANY',
          staffList: Array.isArray(row.staff_list) ? row.staff_list : [],
        } as const
        applyBookingDay(date, payload)
        return payload
      } catch (e: any) {
        if (!options.quiet) {
          setBookingSlots([])
          setBookingEnabled(true)
          setBookingClosed(false)
          setBookingReasonCode(null)
          setBookingError(e?.response?.data?.detail || e?.message || 'Failed to load availability')
        }
        return null
      } finally {
        if (!options.quiet) {
          setBookingLoading(false)
        }
        setBookingDaySummaries((prev) => {
          if (!prev[date]) return prev
          return { ...prev, [date]: { ...prev[date], loading: false } }
        })
      }
    },
    [applyBookingDay, bookingServiceId, bookingStaffId, bookingStaffMode, slug],
  )

  const loadSelectedBookingDay = useCallback(async (date: string, options: { force?: boolean } = {}) => {
    const payload = await fetchBookingDay(date, { force: options.force })
    if (!payload) return
    setBookingSlots(payload.slots)
    setBookingEnabled(payload.enabled)
    setBookingClosed(payload.closed)
    setBookingReasonCode(payload.reasonCode)
    setBookingDurationMinutes(payload.durationMinutes)
    setBookingTimezone(payload.timezone)
    setBookingStaffList(payload.staffList)
    setBookingStaffMode(payload.staffMode)
  }, [fetchBookingDay])

  useEffect(() => {
    if (!bookingPaymentExpiresAt) {
      setBookingPaymentRemainingSeconds(0)
      return
    }
    const tick = () => {
      const ms = new Date(bookingPaymentExpiresAt).getTime() - Date.now()
      setBookingPaymentRemainingSeconds(Math.max(0, Math.floor(ms / 1000)))
    }
    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [bookingPaymentExpiresAt])

  useEffect(() => {
    if (!bookingClientSecret || bookingPaymentRemainingSeconds > 0) return
    setBookingError('Payment session expired — please pick a time again.')
    setBookingClientSecret('')
    setBookingPaymentExpiresAt(null)
    setLatestBooking(null)
    setBookingOpen(false)
    setBookingSelected(null)
    if (!slug || !bookingDate) return
    void loadSelectedBookingDay(bookingDate, { force: true })
  }, [bookingClientSecret, bookingPaymentRemainingSeconds, slug, bookingDate, loadSelectedBookingDay])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setData(null)
    publicBusiness(slug)
      .then((row) => {
        if (!cancelled) setData(row)
      })
      .catch((e: any) => {
        if (cancelled) return
        if (e?.response?.status === 404) setError('Not found')
        else setError(e?.response?.data?.detail || e?.message || 'Failed to load page')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [slug])

  const heroCover = data?.cover_image_url || data?.pixe_portrait_image_url || ''
  const reputation = useMemo(() => String(data?.reputation_score ?? '0'), [data])
  const storyHtml = useMemo(() => {
    const html = String(data?.story_rendered_html || '').trim()
    if (!html) return ''
    const purifier = (window as any).DOMPurify
    return purifier?.sanitize?.(html) ?? html
  }, [data?.story_rendered_html])
  const sectionNav = useMemo(() => ([
    { id: 'about', label: 'About' },
    { id: 'posts', label: 'Posts' },
    { id: 'offers', label: 'Offers' },
    { id: 'reviews', label: 'Reviews' },
    { id: 'team', label: 'Team' },
    { id: 'booking', label: 'Booking' },
    { id: 'contact', label: 'Contact' },
    { id: 'hours', label: 'Hours' },
    { id: 'comments', label: 'Comments' },
  ] as const), [])

  useEffect(() => {
    const onScroll = () => {
      const ids: SectionKey[] = ['about', 'posts', 'offers', 'reviews', 'team', 'booking', 'contact', 'hours', 'comments']
      let current: SectionKey = 'about'
      for (const id of ids) {
        const el = document.getElementById(id)
        if (!el) continue
        const top = el.getBoundingClientRect().top
        if (top <= 160) current = id
      }
      setActiveSection(current)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScroll)
    }
  }, [])

  const loadReviews = async (targetPage: number, sort: 'newest' | 'highest' | 'lowest', append = false) => {
    setReviewsLoading(true)
    setReviewError(null)
    try {
      const res = await listBusinessReviews(slug, { page: targetPage, page_size: 10, sort })
      setReviewsSummary(res.summary)
      setReviewHasNext(Boolean(res.has_next))
      setReviewPage(res.page || targetPage)
      setReviews((prev) => (append ? [...prev, ...res.results] : res.results))
    } catch (e: any) {
      setReviewError(e?.response?.data?.detail || e?.message || 'Failed to load reviews')
      if (!append) {
        setReviews([])
        setReviewHasNext(false)
      }
    } finally {
      setReviewsLoading(false)
    }
  }

  useEffect(() => {
    if (!slug) return
    loadReviews(1, reviewSort, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, reviewSort])

  useEffect(() => {
    if (!slug) return
    let cancelled = false
    listPublicTeam(slug)
      .then((rows) => {
        if (!cancelled) setTeamMembers(rows)
      })
      .catch(() => {
        if (!cancelled) setTeamMembers([])
      })
    return () => {
      cancelled = true
    }
  }, [slug])

  useEffect(() => {
    if (bookingDate) return
    const today = todayIsoDate()
    setBookingDate(today)
    setBookingVisibleMonth(monthStartIso(today))
  }, [bookingDate])

  useEffect(() => {
    if (!slug) return
    let cancelled = false
    listBusinessPublicBookingServices(slug)
      .then((rows) => {
        if (cancelled) return
        setBookingServices(rows)
        if (!bookingServiceId && rows.length > 0) setBookingServiceId(rows[0].id)
      })
      .catch(() => {
        if (!cancelled) setBookingServices([])
      })
    return () => {
      cancelled = true
    }
  }, [slug])

  useEffect(() => {
    if (!slug || !bookingDate) return
    void loadSelectedBookingDay(bookingDate)
  }, [slug, bookingDate, bookingServiceId, bookingStaffMode, bookingStaffId, loadSelectedBookingDay])

  useEffect(() => {
    if (!bookingSelected) return
    const stillAvailable = bookingSlots.some((slot) => slot.starts_at === bookingSelected.starts_at)
    if (!stillAvailable) {
      setBookingSelected(null)
      setBookingOpen(false)
    }
  }, [bookingSlots, bookingSelected])

  useEffect(() => {
    if (!bookingDate) return
    setBookingVisibleMonth(monthStartIso(bookingDate))
  }, [bookingDate])

  useEffect(() => {
    bookingDayCacheRef.current = {}
    setBookingDaySummaries({})
  }, [bookingServiceId, bookingStaffMode, bookingStaffId])

  useEffect(() => {
    if (!slug || !bookingVisibleMonth) return
    let cancelled = false
    const monthDates = getMonthDates(bookingVisibleMonth)
    const queue = monthDates.filter((date) => !bookingDayCacheRef.current[date])
    if (!queue.length) return
    let cursor = 0
    const worker = async () => {
      while (!cancelled) {
        const idx = cursor
        cursor += 1
        if (idx >= queue.length) return
        await fetchBookingDay(queue[idx], { quiet: true })
      }
    }
    void Promise.all([worker(), worker(), worker()])
    return () => {
      cancelled = true
    }
  }, [bookingVisibleMonth, bookingServiceId, fetchBookingDay, slug])

  useEffect(() => {
    if (!slug) return
    let cancelled = false
    quoteBooking({ host_type: 'business', host_ref: slug, service_id: bookingServiceId ?? undefined })
      .then((q) => {
        if (!cancelled) setBookingQuote(q)
      })
      .catch(() => {
        if (!cancelled) setBookingQuote(null)
      })
    return () => {
      cancelled = true
    }
  }, [slug, bookingServiceId])

  useEffect(() => {
    if (!slug) return
    let cancelled = false
    businessMetricsSummary(slug)
      .then((res) => {
        if (!cancelled) setMetrics(res)
      })
      .catch(() => {
        if (!cancelled) setMetrics(null)
      })
    return () => {
      cancelled = true
    }
  }, [slug])

  useEffect(() => {
    if (!slug) return
    let cancelled = false
    setOffersLoading(true)
    setOffersError(null)
    listBusinessOffers(slug)
      .then((rows) => {
        if (!cancelled) setOffers(rows)
      })
      .catch((e: any) => {
        if (cancelled) return
        setOffers([])
        setOffersError(e?.response?.data?.detail || e?.message || 'Failed to load offers')
      })
      .finally(() => {
        if (!cancelled) setOffersLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [slug])

  useEffect(() => {
    if (!slug) return
    let cancelled = false
    setHoursLoading(true)
    setHoursError(null)
    listBusinessHours(slug)
      .then((row) => {
        if (!cancelled) setHoursData(row)
      })
      .catch((e: any) => {
        if (cancelled) return
        setHoursData(null)
        setHoursError(e?.response?.data?.detail || e?.message || 'Failed to load hours')
      })
      .finally(() => {
        if (!cancelled) setHoursLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [slug])

  const loadComments = async (targetPage: number, append = false) => {
    setCommentsLoading(true)
    setCommentsError(null)
    try {
      const res = await listBusinessComments(slug, { page: targetPage, page_size: 20 })
      setCommentsHasNext(Boolean(res.has_next))
      setCommentsPage(Number(res.page || targetPage))
      setComments((prev) => (append ? [...prev, ...res.results] : res.results))
    } catch (e: any) {
      setCommentsError(e?.response?.data?.detail || e?.message || 'Failed to load comments')
      if (!append) {
        setComments([])
        setCommentsHasNext(false)
      }
    } finally {
      setCommentsLoading(false)
    }
  }

  useEffect(() => {
    if (!slug) return
    loadComments(1, false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])

  useEffect(() => {
    if (window.location.hash !== '#comments') return
    window.setTimeout(() => {
      scrollToSection('comments')
      if (authed) {
        try {
          commentInputRef.current?.focus()
        } catch {}
      }
    }, 120)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, slug])

  const avgRating = Number(reviewsSummary?.avg_rating || '0')
  const totalReviews = Number(reviewsSummary?.total || 0)
  const hoursTimezone = String(hoursData?.timezone || data?.timezone || 'UTC')
  const nowInTz = useMemo(() => getNowInTimezone(hoursTimezone), [hoursTimezone, hoursData?.open_now])
  const specialToday = hoursData?.special_today || null
  const dayRanges = useMemo(() => {
    const source = hoursData?.days || {}
    const out: Record<number, BusinessHourRange[]> = {}
    for (let i = 0; i < 7; i += 1) {
      out[i] = Array.isArray(source[String(i)]) ? source[String(i)] : []
    }
    return out
  }, [hoursData?.days])
  const displayDayRanges = useMemo(() => {
    const out: Record<number, BusinessHourRange[]> = { ...dayRanges }
    if (!specialToday) return out
    const idx = nowInTz.weekday
    if (specialToday.is_closed) {
      out[idx] = []
      return out
    }
    if (specialToday.opens_at && specialToday.closes_at) {
      out[idx] = [{ opens_at: specialToday.opens_at, closes_at: specialToday.closes_at }]
    }
    return out
  }, [dayRanges, nowInTz.weekday, specialToday])
  const currentRange = useMemo(() => {
    const todays = displayDayRanges[nowInTz.weekday] || []
    return todays.find((r) => {
      const open = parseTimeToMinutes(r.opens_at)
      const close = parseTimeToMinutes(r.closes_at)
      return open <= nowInTz.minutes && nowInTz.minutes < close
    }) || null
  }, [displayDayRanges, nowInTz.weekday, nowInTz.minutes])
  const nextOpening = useMemo(() => {
    for (let offset = 0; offset < 7; offset += 1) {
      const day = (nowInTz.weekday + offset) % 7
      const ranges = displayDayRanges[day] || []
      const sorted = [...ranges].sort((a, b) => parseTimeToMinutes(a.opens_at) - parseTimeToMinutes(b.opens_at))
      if (!sorted.length) continue
      if (offset === 0) {
        const laterToday = sorted.find((r) => parseTimeToMinutes(r.opens_at) > nowInTz.minutes)
        if (laterToday) return { day, range: laterToday, offset }
        continue
      }
      return { day, range: sorted[0], offset }
    }
    return null
  }, [displayDayRanges, nowInTz.weekday, nowInTz.minutes])

  useEffect(() => {
    if (!slug) return
    let cancelled = false
    businessActionsSummary(slug)
      .then((summary) => {
        if (cancelled) return
        setRailLiked(Boolean(summary.viewer?.liked))
        setRailSaved(Boolean(summary.viewer?.saved))
        setRailLikesCount(Number(summary.like_count || 0))
        setRailCommentsCount(Number(summary.comment_count || 0))
        setRailSavesCount(Number(summary.save_count || 0))
        setRailSharesCount(Number(summary.share_count || 0))
        setRailGiftCount(Number(summary.gift_count || 0))
        setRailGiftTotal(String(summary.gift_total_pixe || '0.00'))
      })
      .catch(() => {
        if (cancelled) return
        setRailLiked(false)
        setRailSaved(false)
        setRailLikesCount(0)
        setRailCommentsCount(0)
        setRailSavesCount(0)
        setRailSharesCount(0)
        setRailGiftCount(0)
        setRailGiftTotal('0.00')
      })
    return () => {
      cancelled = true
    }
  }, [slug])

  const getAnonId = () => {
    const key = 'pixeloop_anon_id'
    try {
      const existing = localStorage.getItem(key)
      if (existing) return existing
      const next = `anon_${Math.random().toString(36).slice(2)}_${Date.now()}`
      localStorage.setItem(key, next)
      return next
    } catch {
      return ''
    }
  }

  const onCall = async () => {
    if (!data?.phone || callBusy) return
    setCallBusy(true)
    setCallMsg(null)
    try {
      const res = await callClick(data.slug, 'READER', getAnonId(), {
        cat: data.category || '',
        placement_context: 'READER',
      })
      const finalPrice = res.final_price_pixe || res.price_pixe
      setCallMsg(res.charged ? `Calling... (Business may pay ${finalPrice} PIXE for this lead)` : 'Calling... Lead logged (not charged).')
    } catch {
      setCallMsg('Calling...')
    } finally {
      const tel = `tel:${String(data.phone).replace(/\s+/g, '')}`
      window.location.href = tel
      setCallBusy(false)
    }
  }

  const onOfferCta = async (offer: BusinessOffer) => {
    if (offer.cta_type === 'NONE') return
    if (offer.cta_type === 'CALL') {
      await onCall()
      return
    }
    const target = offer.cta_type === 'WEBSITE'
      ? (offer.cta_url || data?.website || '')
      : (offer.cta_url || '')
    if (!target) return
    window.open(target, '_blank', 'noopener,noreferrer')
  }

  const onCreateBooking = async () => {
    if (!bookingSelected || !data || bookingSubmitting) return
    const name = bookingName.trim()
    const email = bookingEmail.trim()
    if (!name || !email) {
      setBookingError('Name and email are required.')
      return
    }
    setBookingSubmitting(true)
    setBookingError(null)
    try {
      const created = await createBooking({
        host_type: 'business',
        host_ref: data.slug,
        service_id: bookingServiceId,
        staff_mode: bookingStaffMode,
        staff_id: bookingStaffMode === 'SPECIFIC' ? bookingStaffId : null,
        starts_at: bookingSelected.starts_at,
        customer_name: name,
        customer_email: email,
        customer_phone: bookingPhone.trim(),
        notes: bookingNotes.trim(),
      })
      if (created.client_secret) {
        setLatestBooking({
          public_id: created.booking_public_id || created.public_id,
          starts_at: created.starts_at,
          ends_at: created.ends_at,
          payment_status: created.payment_status,
        })
        setBookingClientSecret(created.client_secret)
        setBookingPaymentExpiresAt(created.payment_expires_at || null)
        setToast({
          visible: true,
          kind: 'success',
          emoji: 'OK',
          label: 'Complete payment within MM:SS',
          detail: 'We emailed you a payment link to manage this booking.',
        })
        return
      }
      setLatestBooking({
        public_id: created.booking_public_id || created.public_id,
        starts_at: created.starts_at,
        ends_at: created.ends_at,
        payment_status: created.payment_status,
      })
      setBookingOpen(false)
      setBookingSelected(null)
      setBookingClientSecret('')
      setBookingPaymentExpiresAt(null)
      setBookingNotes('')
      const confirmationDetail = created.customer_email ? `Confirmed. We emailed a manage link to ${created.customer_email}.` : undefined
      setToast({ visible: true, kind: 'success', emoji: 'OK', label: 'Booking confirmed', detail: confirmationDetail })
      await loadSelectedBookingDay(bookingDate, { force: true })
    } catch (e: any) {
      const code = e?.response?.data?.code
      if (code === 'SLOT_TAKEN') setBookingError('That slot was just taken. Please choose another.')
      else if (code === 'CAPACITY_FULL') setBookingError('That slot is at capacity. Please choose another.')
      else if (code === 'STAFF_UNAVAILABLE') setBookingError('Selected staff is unavailable for that slot.')
      else if (code === 'RATE_LIMITED') setBookingError('Too many booking attempts. Please try again later.')
      else setBookingError(e?.response?.data?.detail || e?.message || 'Failed to create booking')
    } finally {
      setBookingSubmitting(false)
    }
  }

  const onBookingPaymentSuccess = async () => {
    if (!data) return
    setBookingOpen(false)
    setBookingSelected(null)
    setBookingClientSecret('')
    setBookingPaymentExpiresAt(null)
    setBookingNotes('')
    setLatestBooking((prev) => prev ? ({ ...prev, payment_status: 'PAID' }) : prev)
    setToast({ visible: true, kind: 'success', emoji: 'OK', label: 'Booking deposit paid' })
    try {
      await loadSelectedBookingDay(bookingDate, { force: true })
    } catch {}
  }

  const onCancelLatestBooking = async () => {
    if (!latestBooking || bookingActionBusy) return
    if (!window.confirm('Cancel this booking?')) return
    const reason = window.prompt('Cancel reason (optional):', '') || ''
    setBookingActionBusy(true)
    setBookingError(null)
    try {
      await cancelBookingWithReason(latestBooking.public_id, reason.trim())
      setLatestBooking(null)
      if (!slug || !bookingDate) return
      await loadSelectedBookingDay(bookingDate, { force: true })
      setToast({ visible: true, kind: 'success', emoji: 'OK', label: 'Booking cancelled' })
    } catch (e: any) {
      setBookingError(e?.response?.data?.detail || e?.message || 'Failed to cancel booking')
    } finally {
      setBookingActionBusy(false)
    }
  }

  const onSubmitRescheduleLatestBooking = async () => {
    if (!latestBooking || bookingActionBusy) return
    const nextIso = fromDateTimeLocalValue(bookingRescheduleDateTime)
    if (!nextIso) {
      setBookingError('Select a valid date and time to reschedule.')
      return
    }
    setBookingActionBusy(true)
    setBookingError(null)
    try {
      const updated = await rescheduleBooking(latestBooking.public_id, nextIso)
      setLatestBooking({
        public_id: updated.public_id,
        starts_at: updated.starts_at,
        ends_at: updated.ends_at,
        payment_status: updated.payment_status,
      })
      setBookingRescheduleOpen(false)
      setBookingRescheduleDateTime('')
      if (!slug || !bookingDate) return
      await loadSelectedBookingDay(bookingDate, { force: true })
      setToast({ visible: true, kind: 'success', emoji: 'OK', label: 'Booking rescheduled' })
    } catch (e: any) {
      const code = e?.response?.data?.code
      if (code === 'SLOT_TAKEN') setBookingError('That slot is unavailable. Choose another time.')
      else if (code === 'CAPACITY_FULL') setBookingError('That slot is at capacity. Choose another time.')
      else if (code === 'STAFF_UNAVAILABLE') setBookingError('Assigned staff is unavailable. Choose another time.')
      else if (code === 'EXPIRED') setBookingError('This unpaid booking has expired. Please book again.')
      else setBookingError(e?.response?.data?.detail || e?.message || 'Failed to reschedule booking')
    } finally {
      setBookingActionBusy(false)
    }
  }

  const onFindNextAvailableBookingDay = async () => {
    if (!slug || !bookingDate || bookingFindingNext) return
    setBookingFindingNext(true)
    setBookingError(null)
    const maxDays = Math.max(1, Math.min(30, Number(bookingQuote?.duration_minutes ? 30 : 30)))
    try {
      for (let offset = 1; offset <= maxDays; offset += 1) {
        const date = addDaysIso(bookingDate, offset)
        const payload = await fetchBookingDay(date, { quiet: true })
        if (payload && payload.enabled && payload.slots.length > 0) {
          setBookingDate(date)
          setBookingVisibleMonth(monthStartIso(date))
          setBookingSelected(payload.slots[0])
          setBookingOpen(false)
          setTimeout(() => {
            bookingSlotsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }, 0)
          return
        }
      }
      setBookingError('No upcoming availability in the next 30 days.')
    } finally {
      setBookingFindingNext(false)
    }
  }

  const bookingEmptyMessage = useMemo(() => {
    if (bookingReasonCode === 'DISABLED' || !bookingEnabled) return 'Booking is currently disabled for this page.'
    if (bookingReasonCode === 'CLOSED_SPECIAL') return 'Closed for a special/holiday schedule on this date.'
    if (bookingReasonCode === 'NO_HOURS' || bookingClosed) return 'No operating hours configured for this date.'
    if (bookingReasonCode === 'OUTSIDE_WINDOW') return 'This date is outside the current booking window.'
    if (bookingReasonCode === 'LEAD_TIME') return 'No slots meet the minimum lead time yet.'
    return 'No slots available for this date.'
  }, [bookingReasonCode, bookingEnabled, bookingClosed])

  const bookingDisplayTimezone = String(bookingTimezone || data?.timezone || 'UTC')
  const bookingSelectedService = useMemo(
    () => bookingServices.find((service) => service.id === bookingServiceId) ?? null,
    [bookingServices, bookingServiceId],
  )
  const bookingSelectedSummary = useMemo(() => {
    if (!bookingSelected) return null
    const starts = new Date(bookingSelected.starts_at)
    const ends = new Date(bookingSelected.ends_at)
    return {
      dateLabel: starts.toLocaleDateString([], {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        timeZone: bookingDisplayTimezone,
      }),
      timeLabel: `${starts.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZone: bookingDisplayTimezone })} - ${ends.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZone: bookingDisplayTimezone })}`,
    }
  }, [bookingSelected, bookingDisplayTimezone])

  const isOfferEndingSoon = (offer: BusinessOffer) => {
    if (!offer.ends_at) return false
    const endsMs = new Date(offer.ends_at).getTime()
    if (!Number.isFinite(endsMs)) return false
    const diff = endsMs - Date.now()
    return diff > 0 && diff <= 48 * 60 * 60 * 1000
  }

  const scrollToSection = (id: SectionKey) => {
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const onWriteReviewClick = () => {
    if (!authed) {
      navigate(`/login?return_to=${encodeURIComponent(`/b/${slug}#reviews`)}`)
      return
    }
    setShowReviewComposer(true)
  }

  const onOpenCommentsFromRail = () => {
    if (!authed) {
      navigate(`/login?return_to=${encodeURIComponent(`/b/${slug}#comments`)}`)
      return
    }
    scrollToSection('comments')
    window.setTimeout(() => {
      try {
        commentInputRef.current?.focus()
      } catch {}
    }, 250)
  }

  const onGiftFromRail = () => {
    if (!authed) {
      navigate(`/login?return_to=${encodeURIComponent(`/b/${slug}`)}`)
      return
    }
    setGiftOpen(true)
  }

  const onRailLike = async (nextLiked: boolean) => {
    if (!authed) {
      navigate(`/login?return_to=${encodeURIComponent(`/b/${slug}`)}`)
      return
    }
    const resp = nextLiked ? await likeBusiness(slug) : await unlikeBusiness(slug)
    setRailLiked(Boolean(resp.liked))
    setRailLikesCount(Number(resp.likes || 0))
    setMetrics((prev) => prev ? {
      ...prev,
      likes_count: Number(resp.likes || 0),
      reactions_count: Number(resp.likes || 0) + Number(prev.comments_count || 0),
    } : prev)
  }

  const onRailSave = async (nextSaved: boolean) => {
    if (!authed) {
      navigate(`/login?return_to=${encodeURIComponent(`/b/${slug}`)}`)
      return
    }
    const resp = nextSaved ? await saveBusiness(slug) : await unsaveBusiness(slug)
    setRailSaved(Boolean(resp.saved))
    setRailSavesCount(Number(resp.saves || 0))
  }

  const onRailShare = async () => {
    const url = window.location.href
    try {
      if ((navigator as any).share) {
        await (navigator as any).share({ url, title: 'Check this page on Pixeloop' })
      } else {
        await navigator.clipboard.writeText(url)
      }
      const resp = await shareBusiness(slug)
      setRailSharesCount(Number(resp.shares || 0))
    } catch {}
  }

  const onSendGiftFromDrawer = async ({ gift }: { gift: any }) => {
    if (!data || giftSending) return
    setGiftSending(true)
    try {
      const res = await sendBusinessGift(data.slug, {
        gift_id: gift.id,
        idempotency_key: newIdempotencyKey('bizgift'),
      })
      setRailGiftCount((v) => v + 1)
      setRailGiftTotal((prev) => {
        const next = Number(prev || '0') + Number(res.amount_pixe || '0')
        return next.toFixed(2)
      })
      setMetrics((prev) => prev ? {
        ...prev,
        gifts_count: Number(prev.gifts_count || 0) + 1,
        gifts_total_pixe: (Number(prev.gifts_total_pixe || '0') + Number(res.amount_pixe || '0')).toFixed(2),
      } : prev)
      setToast({
        visible: true,
        kind: 'success',
        emoji: gift.emoji || '🎁',
        label: `Sent ${gift.name}`,
        detail: res.ledger_entry_id ? `Receipt #${res.ledger_entry_id}` : undefined,
      })
      setGiftOpen(false)
    } catch (e: any) {
      setToast({
        visible: true,
        kind: 'error',
        emoji: '⚠️',
        label: 'Gift failed',
        detail: e?.response?.data?.detail || e?.message || 'Please try again',
      })
    } finally {
      setGiftSending(false)
    }
  }

  const onSubmitReview = async () => {
    if (!authed) {
      navigate(`/login?return_to=${encodeURIComponent(`/b/${slug}#reviews`)}`)
      return
    }
    setReviewSubmitting(true)
    setReviewError(null)
    try {
      await createBusinessReview(slug, {
        rating: reviewRating,
        body: reviewBody.trim() || undefined,
      })
      setShowReviewComposer(false)
      setReviewBody('')
      setReviewRating(5)
      await loadReviews(1, reviewSort, false)
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.response?.data?.body || e?.message || 'Failed to post review'
      setReviewError(String(detail))
    } finally {
      setReviewSubmitting(false)
    }
  }

  const onSubmitComment = async () => {
    if (!authed) {
      navigate(`/login?return_to=${encodeURIComponent(`/b/${slug}#comments`)}`)
      return
    }
    const body = commentBody.trim()
    if (!body || commentPosting) return
    setCommentPosting(true)
    setCommentsError(null)
    try {
      const created = await createBusinessComment(slug, { body })
      setComments((prev) => [created, ...prev])
      setCommentBody('')
      setRailCommentsCount((v) => v + 1)
      setMetrics((prev) => prev ? {
        ...prev,
        comments_count: Number(prev.comments_count || 0) + 1,
        reactions_count: Number(prev.reactions_count || 0) + 1,
      } : prev)
      setToast({
        visible: true,
        kind: 'success',
        emoji: '💬',
        label: 'Comment posted',
      })
    } catch (e: any) {
      setCommentsError(e?.response?.data?.detail || e?.response?.data?.body || e?.message || 'Failed to post comment')
    } finally {
      setCommentPosting(false)
    }
  }

  const onFollowToggle = async () => {
    if (!data || followPending) return
    if (!authed) {
      navigate(`/login?return_to=${encodeURIComponent(`/b/${slug}`)}`)
      return
    }
    const prev = metrics
    const isFollowing = Boolean(prev?.viewer?.following)
    const optimisticFollowers = Math.max(0, Number(prev?.followers_count || 0) + (isFollowing ? -1 : 1))
    setFollowPending(true)
    setMetrics((m) => m ? {
      ...m,
      followers_count: optimisticFollowers,
      viewer: { ...m.viewer, following: !isFollowing },
    } : m)
    try {
      const resp = isFollowing ? await unfollowBusiness(slug) : await followBusiness(slug)
      setMetrics((m) => ({
        followers_count: Number(resp.followers_count || 0),
        following_count: Number(m?.following_count || 0),
        gifts_count: Number(m?.gifts_count || 0),
        gifts_total_pixe: String(m?.gifts_total_pixe || '0.00'),
        reactions_count: Number(m?.reactions_count || 0),
        likes_count: Number(m?.likes_count || 0),
        comments_count: Number(m?.comments_count || 0),
        viewer: { following: Boolean(resp.following) },
      }))
    } catch {
      setMetrics(prev)
    } finally {
      setFollowPending(false)
    }
  }

  return (
    <div className="min-h-screen">
      <NavBar showMobileModePill={false} />

      <main className="mx-auto max-w-6xl px-4 pb-[calc(env(safe-area-inset-bottom)+7rem)] pt-4 scroll-smooth md:py-6 md:pb-6">
        {loading ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-sm text-white/75">Loading page...</div>
        ) : null}

        {!loading && error === 'Not found' ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-8">
            <h1 className="text-xl font-extrabold">Business page not found</h1>
            <p className="mt-2 text-sm text-white/70">This page is unavailable or not publicly active yet.</p>
            <Link to="/pages" className="mt-4 inline-flex rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600">
              Back to Pages
            </Link>
          </div>
        ) : null}

        {!loading && error && error !== 'Not found' ? <div className="text-sm text-red-300">{error}</div> : null}

        {!loading && data ? (
          <>
            <section className="mb-3 relative overflow-hidden rounded-2xl border border-white/10 bg-black/25">
              <div className="relative aspect-[4/3] md:aspect-[16/9]">
                {heroCover ? (
                  <img src={heroCover} alt={`${data.name} cover`} className="absolute inset-0 h-full w-full object-cover" />
                ) : (
                  <div className="absolute inset-0 bg-white/5" />
                )}
                <div className="absolute inset-0 pointer-events-none hidden md:block">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-transparent" />
                  <div className="absolute inset-0 bg-[radial-gradient(75%_60%_at_50%_100%,rgba(0,0,0,0.55),transparent_70%)]" />
                </div>
                <div className="hidden md:absolute md:bottom-4 md:left-4 md:right-4 md:flex md:flex-row md:flex-wrap md:items-end md:justify-between md:gap-3">
                  <div className="flex items-start gap-3">
                    <BusinessAvatar name={data.name} logoImageUrl={data.logo_image_url} size={84} className="ring-2 ring-white/20 shadow-xl" />
                    <div className="rounded-xl bg-black/30 px-3 py-2 backdrop-blur">
                      <h1 className="text-2xl font-extrabold text-white">{data.name}</h1>
                      <div className="text-sm text-white/80">@{data.slug}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                        <span className="rounded-full border border-white/20 bg-white/10 px-2.5 py-1 text-white/90">{data.category}</span>
                        <span className="rounded-full border border-emerald-500/30 bg-emerald-500/15 px-2.5 py-1 text-emerald-100">
                          {data.setup_fee_paid ? 'Active' : 'Pending'}
                        </span>
                        <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-white/85">Reputation {reputation}</span>
                        <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-white/85">
                          ★ {totalReviews > 0 ? avgRating.toFixed(1) : '—'} ({totalReviews})
                        </span>
                        <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-white/85">
                          Gifts {railGiftCount} ({railGiftTotal} PIXE)
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={onFollowToggle}
                      disabled={followPending}
                      className={`rounded-full px-4 py-2 text-sm font-semibold shadow-lg ${followPending ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : (metrics?.viewer?.following ? 'border border-white/20 bg-white/10 text-white' : 'bg-brand text-white hover:bg-brand-600')}`}
                    >
                      {followPending ? 'Working...' : (metrics?.viewer?.following ? 'Following' : 'Follow')}
                    </button>
                    <button
                      type="button"
                      onClick={onCall}
                      disabled={callBusy || !data.phone}
                      title={data.phone ? '' : 'Add phone in settings'}
                      className={`rounded-full px-4 py-2 text-sm font-semibold shadow-lg ${callBusy || !data.phone ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'bg-brand text-white hover:bg-brand-600'}`}
                    >
                      {callBusy ? 'Calling...' : 'Call'}
                    </button>
                    {data.website ? (
                      <a
                        href={data.website}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white/90 shadow-lg hover:bg-white/10"
                      >
                        Website
                      </a>
                    ) : null}
                    <button
                      type="button"
                      disabled
                      title="Directions coming soon"
                      className="rounded-full border border-white/10 bg-white/10 px-4 py-2 text-sm font-semibold text-white/45 cursor-not-allowed"
                    >
                      Directions
                    </button>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-4 rounded-2xl border border-white/10 bg-white/5 p-3 md:hidden">
              <div className="flex items-start gap-3">
                <BusinessAvatar name={data.name} logoImageUrl={data.logo_image_url} size={64} className="ring-2 ring-white/20 shadow-xl" />
                <div className="min-w-0">
                  <h1 className="truncate text-xl font-extrabold text-white">{data.name}</h1>
                  <div className="truncate text-sm text-white/75">@{data.slug}</div>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                <span className="rounded-full border border-white/20 bg-white/10 px-2.5 py-1 text-white/90">{data.category}</span>
                <span className="rounded-full border border-emerald-500/30 bg-emerald-500/15 px-2.5 py-1 text-emerald-100">
                  {data.setup_fee_paid ? 'Active' : 'Pending'}
                </span>
              </div>

              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-xl border border-white/10 bg-black/20 p-2">
                  <div className="text-sm font-bold">{reputation}</div>
                  <div className="text-[11px] text-white/65">Reputation</div>
                </div>
                <div className="rounded-xl border border-white/10 bg-black/20 p-2">
                  <div className="text-sm font-bold">{totalReviews > 0 ? avgRating.toFixed(1) : '0.0'}</div>
                  <div className="text-[11px] text-white/65">Reviews</div>
                </div>
                <div className="rounded-xl border border-white/10 bg-black/20 p-2" title={`Total PIXE: ${railGiftTotal}`}>
                  <div className="text-sm font-bold">{railGiftCount}</div>
                  <div className="text-[11px] text-white/65">Gifts</div>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={onFollowToggle}
                  disabled={followPending}
                  className={`min-h-11 rounded-full px-4 py-2 text-sm font-semibold shadow-lg ${followPending ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : (metrics?.viewer?.following ? 'border border-white/20 bg-white/10 text-white' : 'bg-brand text-white hover:bg-brand-600')}`}
                >
                  {followPending ? 'Working...' : (metrics?.viewer?.following ? 'Following' : 'Follow')}
                </button>
                <button
                  type="button"
                  onClick={onCall}
                  disabled={callBusy || !data.phone}
                  title={data.phone ? '' : 'Add phone in settings'}
                  className={`min-h-11 rounded-full px-4 py-2 text-sm font-semibold shadow-lg ${callBusy || !data.phone ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'bg-brand text-white hover:bg-brand-600'}`}
                >
                  {callBusy ? 'Calling...' : 'Call'}
                </button>
                {data.website ? (
                  <a
                    href={data.website}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex min-h-11 items-center justify-center rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white/90 hover:bg-white/10"
                  >
                    Website
                  </a>
                ) : (
                  <button
                    type="button"
                    onClick={() => scrollToSection('contact')}
                    className="min-h-11 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white/90 hover:bg-white/10"
                  >
                    Contact
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => scrollToSection('contact')}
                  className="min-h-11 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white/90 hover:bg-white/10"
                >
                  More
                </button>
              </div>
            </section>

            <div className="mb-4 hidden grid-cols-3 gap-3 text-center md:grid">
              <div className="rounded-xl p-2">
                <div className="text-lg font-extrabold">{Number(metrics?.followers_count || 0)}</div>
                <div className="text-xs text-white/70">Followers</div>
              </div>
              <div
                className="rounded-xl p-2"
                title={`Total PIXE: ${metrics?.gifts_total_pixe || '0.00'}`}
              >
                <div className="text-lg font-extrabold">{Number(metrics?.gifts_count || 0)}</div>
                <div className="text-xs text-white/70">Gifts</div>
              </div>
              <button
                type="button"
                onClick={() => scrollToSection('comments')}
                className="rounded-xl p-2 transition hover:bg-white/5"
              >
                <div className="text-lg font-extrabold">{Number(metrics?.reactions_count || 0)}</div>
                <div className="text-xs text-white/70">Reactions</div>
              </button>
            </div>

            <div className="sticky top-[calc(var(--nav-h,56px)+0.5rem)] z-30 -mx-4 mb-6 border-y border-white/10 bg-black/75 backdrop-blur md:top-0 md:border-b md:border-t-0 md:bg-black/35">
              <nav className="no-scrollbar mx-auto flex max-w-6xl items-center gap-2 overflow-x-auto px-4 py-2.5 whitespace-nowrap">
                {sectionNav.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`min-h-11 shrink-0 rounded-full px-3 py-2 text-sm ${activeSection === item.id ? 'border border-brand/50 bg-brand/15 text-white font-extrabold' : 'text-white/75 hover:bg-white/10 hover:text-white'}`}
                  >
                    {item.label}
                  </button>
                ))}
              </nav>
            </div>

            <section id="about" className="mb-10 scroll-mt-[calc(var(--nav-h,56px)+4.5rem)] md:scroll-mt-24">
              <h2 className="mb-4 text-lg font-extrabold">About</h2>
              <BusinessStory
                title={data.story_title || ''}
                subtitle={data.story_subtitle || ''}
                renderedHtml={storyHtml}
                fallbackDescription={data.description || ''}
              />
              {callMsg ? <div className="mx-auto mt-3 max-w-3xl text-xs text-white/70">{callMsg}</div> : null}
              {(data.recording_enabled || data.transcription_enabled) ? (
                <div className="mx-auto mt-2 max-w-3xl text-xs text-white/65">Calls may be recorded/transcribed.</div>
              ) : null}
            </section>

            <section id="posts" className="mb-10 scroll-mt-[calc(var(--nav-h,56px)+4.5rem)] md:scroll-mt-24">
              <h2 className="mb-4 text-lg font-extrabold">Posts</h2>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-sm text-white/70">
                Business posts coming soon.
              </div>
            </section>

            <section id="offers" className="mb-10 scroll-mt-[calc(var(--nav-h,56px)+4.5rem)] md:scroll-mt-24">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="text-lg font-extrabold">Offers</h2>
                {offers.length > 0 ? (
                  <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-xs text-white/80">
                    {offers.length} active
                  </span>
                ) : null}
              </div>
              {offersLoading ? <div className="text-sm text-white/70">Loading offers...</div> : null}
              {!offersLoading && offersError ? <div className="text-sm text-red-300">{offersError}</div> : null}
              {!offersLoading && !offersError && offers.length === 0 ? (
                <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-sm text-white/70">No offers right now.</div>
              ) : null}
              {!offersLoading && offers.length > 0 ? (
                <div className="grid gap-4 md:grid-cols-2">
                  {offers.map((offer) => (
                    <article key={offer.id} className="overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.09] to-white/[0.04] shadow-[0_10px_30px_rgba(0,0,0,0.35)]">
                      {offer.image_url ? (
                        <div className="relative h-44 w-full bg-black/20">
                          <img src={offer.image_url} alt={offer.title} className="h-full w-full object-cover" />
                          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                          <div className="absolute left-3 top-3 flex items-center gap-2">
                            <span className="rounded-full border border-white/20 bg-black/45 px-2 py-0.5 text-[11px] font-semibold text-white/95">
                              Offer
                            </span>
                            {isOfferEndingSoon(offer) ? (
                              <span className="rounded-full border border-amber-500/30 bg-amber-500/20 px-2 py-0.5 text-[11px] font-semibold text-amber-100">
                                Ends soon
                              </span>
                            ) : null}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between border-b border-white/10 bg-black/20 px-4 py-2">
                          <span className="rounded-full border border-white/20 bg-white/10 px-2 py-0.5 text-[11px] font-semibold text-white/90">
                            Offer
                          </span>
                          {isOfferEndingSoon(offer) ? (
                            <span className="rounded-full border border-amber-500/30 bg-amber-500/20 px-2 py-0.5 text-[11px] font-semibold text-amber-100">
                              Ends soon
                            </span>
                            ) : null}
                        </div>
                      )}
                      <div className="p-4">
                        <div className="text-lg font-extrabold tracking-tight text-white">{offer.title}</div>
                        {offer.body ? <p className="mt-2 text-sm leading-relaxed text-white/75 line-clamp-3">{offer.body}</p> : null}
                        {offer.ends_at ? <div className="mt-3 text-xs text-white/55">Ends {new Date(offer.ends_at).toLocaleString()}</div> : null}
                        {offer.cta_type !== 'NONE' ? (
                          <div className="mt-4 flex items-center justify-between">
                            <button
                              type="button"
                              onClick={() => onOfferCta(offer)}
                              className="rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600"
                            >
                              {offer.cta_type === 'CALL' ? 'Call' : offer.cta_type === 'WEBSITE' ? 'Visit Website' : 'Open Link'}
                            </button>
                            <span className="text-[11px] uppercase tracking-wide text-white/45">{offer.cta_type}</span>
                          </div>
                        ) : null}
                      </div>
                    </article>
                  ))}
                </div>
              ) : null}
            </section>

            <section id="reviews" className="mb-10 scroll-mt-[calc(var(--nav-h,56px)+4.5rem)] md:scroll-mt-24">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-extrabold">Reviews</h2>
                <div className="flex items-center gap-2">
                  <select
                    value={reviewSort}
                    onChange={(e) => setReviewSort(e.target.value as 'newest' | 'highest' | 'lowest')}
                    className="rounded-lg border border-white/15 bg-black/30 px-3 py-1.5 text-sm text-white outline-none"
                  >
                    <option value="newest">Newest</option>
                    <option value="highest">Highest</option>
                    <option value="lowest">Lowest</option>
                  </select>
                  <button
                    type="button"
                    onClick={onWriteReviewClick}
                    className="rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
                  >
                    Write a review
                  </button>
                </div>
              </div>

              <div className="mb-4 rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="grid gap-4 md:grid-cols-[200px_1fr]">
                  <div>
                    <div className="text-3xl font-extrabold">{totalReviews > 0 ? avgRating.toFixed(1) : '0.0'}</div>
                    <div className="mt-1 text-sm text-white/70">Based on {totalReviews} review{totalReviews === 1 ? '' : 's'}</div>
                  </div>
                  <div className="space-y-1.5">
                    {[5, 4, 3, 2, 1].map((star) => {
                      const count = Number(reviewsSummary?.counts?.[String(star) as '5' | '4' | '3' | '2' | '1'] || 0)
                      const pct = totalReviews > 0 ? (count / totalReviews) * 100 : 0
                      return (
                        <div key={star} className="flex items-center gap-2 text-xs">
                          <div className="w-10 text-white/80">{star}★</div>
                          <div className="h-2 flex-1 overflow-hidden rounded bg-white/10">
                            <div className="h-full bg-brand" style={{ width: `${pct}%` }} />
                          </div>
                          <div className="w-8 text-right text-white/65">{count}</div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              </div>

              {showReviewComposer ? (
                <div className="mb-4 rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="mb-3 text-sm font-semibold">Your rating</div>
                  <div className="mb-3 flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setReviewRating(star)}
                        className={`text-2xl ${reviewRating >= star ? 'text-yellow-400' : 'text-white/35'}`}
                      >
                        ★
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={reviewBody}
                    onChange={(e) => setReviewBody(e.target.value)}
                    placeholder="Share your experience"
                    className="min-h-[110px] w-full rounded-xl border border-white/10 bg-black/30 p-3 text-sm text-white outline-none focus:ring-2 focus:ring-brand"
                  />
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={onSubmitReview}
                      disabled={reviewSubmitting}
                      className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
                    >
                      {reviewSubmitting ? 'Submitting...' : 'Submit review'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowReviewComposer(false)}
                      disabled={reviewSubmitting}
                      className="rounded-lg border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white/90 hover:bg-white/10 disabled:opacity-60"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : null}

              {reviewError ? <div className="mb-3 text-sm text-red-300">{reviewError}</div> : null}
              {reviewsLoading && reviews.length === 0 ? <div className="text-sm text-white/70">Loading reviews...</div> : null}
              {!reviewsLoading && reviews.length === 0 ? (
                <div className="rounded-2xl border border-white/10 bg-white/5 p-5 text-sm text-white/70">No reviews yet.</div>
              ) : null}
              {reviews.length > 0 ? (
                <div className="space-y-3">
                  {reviews.map((review) => (
                    <article key={review.id} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                      <div className="mb-2 flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2">
                          {review.author.avatar_url ? (
                            <img
                              src={review.author.avatar_url}
                              alt={review.author.display_name || review.author.handle || 'Reviewer'}
                              className="h-9 w-9 rounded-full object-cover"
                            />
                          ) : (
                            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-xs font-semibold text-white/85">
                              {(review.author.display_name || review.author.handle || 'U').slice(0, 1).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <div className="text-sm font-semibold">
                              {review.author.display_name || review.author.handle || review.author.username || 'User'}
                            </div>
                            <div className="text-xs text-white/60">
                              @{review.author.handle || review.author.username || 'user'}
                            </div>
                          </div>
                        </div>
                        <div className="text-xs text-white/60">{new Date(review.created_at).toLocaleDateString()}</div>
                      </div>
                      {review.is_verified || review.verified_booking ? (
                        <div className="mb-2 inline-flex items-center rounded-full border border-emerald-400/40 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-semibold text-emerald-200">
                          Verified booking
                        </div>
                      ) : null}
                      <div className="mb-2 text-yellow-400">{'★'.repeat(Math.max(1, Math.min(5, Number(review.rating || 0))))}</div>
                      {review.body ? <p className="text-sm text-white/85 whitespace-pre-wrap">{review.body}</p> : null}
                    </article>
                  ))}
                  {reviewHasNext ? (
                    <button
                      type="button"
                      onClick={() => loadReviews(reviewPage + 1, reviewSort, true)}
                      disabled={reviewsLoading}
                      className="rounded-lg border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white/90 hover:bg-white/10 disabled:opacity-60"
                    >
                      {reviewsLoading ? 'Loading...' : 'Load more'}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </section>

            <section id="team" className="mb-10 scroll-mt-[calc(var(--nav-h,56px)+4.5rem)] md:scroll-mt-24">
              <div className="mb-4 flex items-center justify-between gap-3">
                <h2 className="text-lg font-extrabold">Team</h2>
                {teamMembers.length > 6 ? (
                  <button
                    type="button"
                    onClick={() => setTeamExpanded((v) => !v)}
                    className="text-sm font-semibold text-brand hover:underline"
                  >
                    {teamExpanded ? 'Show less' : 'View all'}
                  </button>
                ) : null}
              </div>
              {teamMembers.length === 0 ? (
                <div className="rounded-2xl border border-white/10 bg-white/5 p-5 text-sm text-white/70">No public team members listed yet.</div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {(teamExpanded ? teamMembers : teamMembers.slice(0, 6)).map((member) => {
                    const handle = member.user.handle || member.user.username
                    const display = member.user.display_name || handle
                    const profilePublicId = (member.user.profile_public_id || '').trim()
                    const memberHref = profilePublicId
                      ? `/people/i/${encodeURIComponent(profilePublicId)}`
                      : `/people`
                    return (
                      <Link
                        key={member.id}
                        to={memberHref}
                        className="rounded-xl border border-white/10 bg-white/5 p-3 hover:bg-white/10"
                      >
                        <div className="flex items-center gap-3">
                          {member.user.avatar_url ? (
                            <img src={member.user.avatar_url} alt={display} className="h-10 w-10 rounded-full object-cover" />
                          ) : (
                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-xs font-semibold text-white/85">
                              {display.slice(0, 1).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <div className="text-sm font-semibold text-white">{display}</div>
                            <div className="text-xs text-white/60">@{handle}</div>
                            <div className="mt-1 text-[11px] text-white/50">{member.public_role_label}</div>
                          </div>
                        </div>
                      </Link>
                    )
                  })}
                </div>
              )}
            </section>

            <section id="booking" className="mb-12 scroll-mt-[calc(var(--nav-h,56px)+4.5rem)] md:scroll-mt-24">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-extrabold">Booking</h2>
                <div className="text-xs text-white/65">Pick a date and time</div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                <div className="mb-3 flex flex-wrap items-end gap-3">
                  {bookingServices.length > 0 ? (
                    <label className="text-sm">
                      <div className="mb-1 text-xs text-white/60">Service</div>
                      <select
                        value={bookingServiceId ?? ''}
                        onChange={(e) => setBookingServiceId(e.target.value ? Number(e.target.value) : null)}
                        className="rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                      >
                        {bookingServices.map((service) => (
                          <option key={service.id} value={service.id}>
                            {service.name} ({service.duration_minutes}m{service.capacity > 1 ? ` • cap ${service.capacity}` : ''}{service.price_cents != null ? ` • $${(service.price_cents / 100).toFixed(2)}` : ''})
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                  {bookingStaffList.length > 0 ? (
                    <label className="text-sm">
                      <div className="mb-1 text-xs text-white/60">Staff</div>
                      <select
                        value={bookingStaffMode === 'SPECIFIC' ? (bookingStaffId ?? '') : ''}
                        onChange={(e) => {
                          const next = e.target.value ? Number(e.target.value) : null
                          setBookingStaffMode(next ? 'SPECIFIC' : 'ANY')
                          setBookingStaffId(next)
                        }}
                        className="rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                      >
                        <option value="">Any staff</option>
                        {bookingStaffList.map((member) => (
                          <option key={member.id} value={member.id}>
                            {member.user.display_name}
                          </option>
                        ))}
                      </select>
                    </label>
                  ) : null}
                  <div className="text-sm">
                    <div className="mb-1 text-xs text-white/60">Date</div>
                    <div className="rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm text-white/90">
                      {bookingDate || 'Select a day'}
                    </div>
                  </div>
                  <div className="text-xs text-white/65">Duration: {bookingDurationMinutes} min</div>
                  <NextAvailableButton loading={bookingFindingNext} onFind={onFindNextAvailableBookingDay} />
                </div>
                <div className="mb-3">
                  <BookingCalendar
                    selectedDate={bookingDate}
                    visibleMonth={bookingVisibleMonth || monthStartIso(bookingDate || todayIsoDate())}
                    onVisibleMonthChange={setBookingVisibleMonth}
                    onSelectDate={setBookingDate}
                    daySummaries={bookingDaySummaries}
                  />
                </div>
                <div className="mb-3 text-xs text-white/60">Times shown in: {bookingDisplayTimezone}</div>
                {bookingError ? <div className="mb-3 text-sm text-red-300">{bookingError}</div> : null}
                {bookingLoading ? <div className="text-sm text-white/70">Loading slots...</div> : null}
                {!bookingLoading && (bookingReasonCode || !bookingEnabled || bookingClosed) ? (
                  <div className="mb-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
                    {bookingEmptyMessage}
                  </div>
                ) : null}
                {!bookingLoading && bookingSlots.length === 0 && !(bookingReasonCode || !bookingEnabled || bookingClosed) ? <div className="text-sm text-white/70">{bookingEmptyMessage}</div> : null}
                {!bookingLoading && bookingSlots.length > 0 ? (
                  <div ref={bookingSlotsRef}>
                    <div className="mb-3 border-t border-white/10 pt-3">
                      <div className="text-sm font-semibold text-white/90">Pick a time</div>
                    </div>
                    <BookingDaySlots
                      slots={bookingSlots}
                      selectedStartAt={bookingSelected?.starts_at || null}
                      timeZone={bookingDisplayTimezone}
                      onPick={(slot) => {
                        setBookingSelected(slot)
                        setBookingOpen(false)
                        setBookingError(null)
                      }}
                    />
                  </div>
                ) : null}
                {bookingSelected && bookingSelectedSummary ? (
                  <div className="sticky bottom-3 z-10 mt-4 rounded-xl border border-brand/30 bg-black/85 p-3 shadow-lg backdrop-blur">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="text-xs text-white/75">
                        <div className="text-sm font-semibold text-white">
                          {bookingSelectedService?.name || 'Appointment'}
                        </div>
                        <div>{bookingSelectedSummary.dateLabel} • {bookingSelectedSummary.timeLabel}</div>
                        <div className="text-white/60">
                          {bookingDurationMinutes} min
                          {bookingSelectedService?.price_cents != null ? ` • $${(bookingSelectedService.price_cents / 100).toFixed(2)}` : ''}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setBookingOpen(true)
                          setBookingError(null)
                        }}
                        className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
                      >
                        Continue
                      </button>
                    </div>
                  </div>
                ) : null}
                {latestBooking ? (
                  <div className="mt-4 rounded-xl border border-white/10 bg-black/20 p-3">
                    <div className="text-sm font-semibold">Latest booking</div>
                    <div className="mt-1 text-xs text-white/70">
                      {new Date(latestBooking.starts_at).toLocaleString()} - {new Date(latestBooking.ends_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                    </div>
                    <div className="mt-1 text-xs text-white/70">Status: {latestBooking.payment_status}</div>
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setBookingRescheduleOpen((v) => !v)
                          setBookingRescheduleDateTime(toDateTimeLocalValue(latestBooking.starts_at))
                        }}
                        disabled={bookingActionBusy}
                        className={`rounded-full px-3 py-1.5 text-xs font-semibold ${bookingActionBusy ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'border border-white/15 bg-white/5 text-white hover:bg-white/10'}`}
                      >
                        Reschedule
                      </button>
                      <button
                        type="button"
                        onClick={onCancelLatestBooking}
                        disabled={bookingActionBusy}
                        className={`rounded-full px-3 py-1.5 text-xs font-semibold ${bookingActionBusy ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'border border-red-400/30 bg-red-500/10 text-red-200 hover:bg-red-500/20'}`}
                      >
                        Cancel
                      </button>
                    </div>
                    {bookingRescheduleOpen ? (
                      <div className="mt-3 flex flex-wrap items-end gap-2">
                        <label className="text-xs text-white/70">
                          New start
                          <input
                            type="datetime-local"
                            value={bookingRescheduleDateTime}
                            onChange={(e) => setBookingRescheduleDateTime(e.target.value)}
                            className="mt-1 block rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                          />
                        </label>
                        <button
                          type="button"
                          onClick={onSubmitRescheduleLatestBooking}
                          disabled={bookingActionBusy || !bookingRescheduleDateTime}
                          className={`rounded-lg px-3 py-2 text-xs font-semibold ${bookingActionBusy || !bookingRescheduleDateTime ? 'border border-white/10 bg-white/10 text-white/50 cursor-not-allowed' : 'bg-brand text-white hover:bg-brand-600'}`}
                        >
                          {bookingActionBusy ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </section>

            <section id="contact" className="mb-12 scroll-mt-[calc(var(--nav-h,56px)+4.5rem)] md:scroll-mt-24">
              <h2 className="mb-4 text-lg font-extrabold">Contact</h2>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-4 text-sm">
                    <div>
                      <div className="mb-1 text-xs uppercase tracking-wide text-white/55">Phone</div>
                      {data.phone ? (
                        <div className="flex items-center gap-2 text-white/90">
                          <span>{data.phone}</span>
                          <CopyButton value={data.phone} />
                        </div>
                      ) : (
                        <div className="text-white/55">Not set</div>
                      )}
                    </div>
                    <div>
                      <div className="mb-1 text-xs uppercase tracking-wide text-white/55">Website</div>
                      {data.website ? (
                        <div className="flex items-center gap-2">
                          <a className="text-brand-300 hover:underline" href={data.website} target="_blank" rel="noreferrer">
                            {data.website}
                          </a>
                          <CopyButton value={data.website} />
                        </div>
                      ) : (
                        <div className="text-white/55">Not set</div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-4 text-sm">
                    <div>
                      <div className="mb-1 text-xs uppercase tracking-wide text-white/55">Address</div>
                      {data.address ? (
                        <div className="flex items-center gap-2 text-white/90">
                          <span>{data.address}</span>
                          <CopyButton value={data.address} />
                        </div>
                      ) : (
                        <div className="text-white/55">Not set</div>
                      )}
                    </div>
                    <div className="pt-1">
                      <button
                        type="button"
                        onClick={onCall}
                        disabled={callBusy || !data.phone}
                        title={data.phone ? '' : 'Add phone in settings'}
                        className={`rounded-full px-4 py-2 text-sm font-semibold ${callBusy || !data.phone ? 'border border-white/10 bg-white/10 text-white/45 cursor-not-allowed' : 'bg-brand text-white hover:bg-brand-600'}`}
                      >
                        {callBusy ? 'Calling...' : 'Call now'}
                      </button>
                    </div>
                  </div>
                </div>
                {!data.website && !data.phone && !data.address ? (
                  <div className="mt-4 text-sm text-white/60">No contact info yet.</div>
                ) : null}
              </div>
            </section>

            <section id="hours" className="mb-12 scroll-mt-[calc(var(--nav-h,56px)+4.5rem)] md:scroll-mt-24">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-extrabold">Hours</h2>
                <div className="flex items-center gap-2">
                  {specialToday ? (
                    <span className="rounded-full border border-amber-500/30 bg-amber-500/15 px-2.5 py-1 text-xs font-semibold text-amber-100">
                      Holiday hours
                    </span>
                  ) : null}
                  {hoursData?.open_now ? (
                    <span className="rounded-full border border-emerald-500/30 bg-emerald-500/15 px-2.5 py-1 text-xs font-semibold text-emerald-200">
                      Open now
                    </span>
                  ) : (
                    <span className="rounded-full border border-white/20 bg-white/10 px-2.5 py-1 text-xs font-semibold text-white/75">
                      Closed
                    </span>
                  )}
                </div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                {hoursLoading ? <div className="text-sm text-white/70">Loading hours...</div> : null}
                {!hoursLoading && hoursError ? <div className="text-sm text-red-300">{hoursError}</div> : null}
                {!hoursLoading && !hoursError ? (
                  <>
                    <div className="mb-3 text-xs uppercase tracking-wide text-white/55">Timezone: {hoursTimezone}</div>
                    {specialToday ? (
                      <div className="mb-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">
                        {specialToday.is_closed ? 'Closed today' : `Holiday hours: ${formatTimeLabel(String(specialToday.opens_at || ''), hoursTimezone)} - ${formatTimeLabel(String(specialToday.closes_at || ''), hoursTimezone)}`}
                        {specialToday.note ? <span className="text-amber-200/90"> • {specialToday.note}</span> : null}
                      </div>
                    ) : null}
                    <div className="space-y-2 text-sm">
                      {WEEKDAY_LABELS.map((label, idx) => {
                        const ranges = displayDayRanges[idx] || []
                        return (
                          <div key={label} className="flex items-center justify-between gap-3">
                            <div className="w-14 text-white/70">{label}</div>
                            <div className="flex-1 text-white/90">
                              {ranges.length
                                ? ranges.map((r) => `${formatTimeLabel(r.opens_at, hoursTimezone)} - ${formatTimeLabel(r.closes_at, hoursTimezone)}`).join(', ')
                                : 'Closed'}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                    {hoursData?.open_now && currentRange ? (
                      <div className="mt-4 text-sm text-emerald-200">
                        Closes at {formatTimeLabel(currentRange.closes_at, hoursTimezone)}
                      </div>
                    ) : null}
                    {!hoursData?.open_now && nextOpening ? (
                      <div className="mt-4 text-sm text-white/75">
                        Closed • Opens {nextOpening.offset === 0 ? 'today' : nextOpening.offset === 1 ? 'tomorrow' : WEEKDAY_LABELS[nextOpening.day]} at{' '}
                        {formatTimeLabel(nextOpening.range.opens_at, hoursTimezone)}
                      </div>
                    ) : null}
                  </>
                ) : null}
              </div>
            </section>

            <section id="comments" className="mb-12 scroll-mt-[calc(var(--nav-h,56px)+4.5rem)] md:scroll-mt-24">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-extrabold">Comments</h2>
                <div className="text-sm text-white/70">{railCommentsCount}</div>
              </div>

              <div className="mb-4 rounded-2xl border border-white/10 bg-white/5 p-4">
                {authed ? (
                  <div className="space-y-2">
                    <textarea
                      ref={commentInputRef}
                      value={commentBody}
                      onChange={(e) => setCommentBody(e.target.value)}
                      placeholder="Leave a comment"
                      className="min-h-[84px] w-full rounded-xl border border-white/10 bg-black/30 p-3 text-sm text-white outline-none focus:ring-2 focus:ring-brand"
                    />
                    <div className="flex items-center justify-end">
                      <button
                        type="button"
                        onClick={onSubmitComment}
                        disabled={commentPosting || !commentBody.trim()}
                        className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
                      >
                        {commentPosting ? 'Posting...' : 'Post'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="text-sm text-white/70">Log in to comment.</div>
                    <button
                      type="button"
                      onClick={() => navigate(`/login?return_to=${encodeURIComponent(`/b/${slug}#comments`)}`)}
                      className="rounded-full bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
                    >
                      Log in to comment
                    </button>
                  </div>
                )}
              </div>

              {commentsError ? <div className="mb-3 text-sm text-red-300">{commentsError}</div> : null}
              {commentsLoading && comments.length === 0 ? <div className="text-sm text-white/70">Loading comments...</div> : null}
              {!commentsLoading && comments.length === 0 ? (
                <div className="rounded-2xl border border-white/10 bg-white/5 p-5 text-sm text-white/70">No comments yet.</div>
              ) : null}
              {comments.length > 0 ? (
                <div className="space-y-3">
                  {comments.map((comment) => (
                    <article key={comment.id} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                      <div className="mb-2 flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2">
                          {comment.author.avatar_url ? (
                            <img
                              src={comment.author.avatar_url}
                              alt={comment.author.display_name || comment.author.handle || 'Commenter'}
                              className="h-9 w-9 rounded-full object-cover"
                            />
                          ) : (
                            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-xs font-semibold text-white/85">
                              {(comment.author.display_name || comment.author.handle || 'U').slice(0, 1).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <div className="text-sm font-semibold">
                              {comment.author.display_name || comment.author.handle || comment.author.username || 'User'}
                            </div>
                            <div className="text-xs text-white/60">
                              @{comment.author.handle || comment.author.username || 'user'}
                            </div>
                          </div>
                        </div>
                        <div className="text-xs text-white/60">{new Date(comment.created_at).toLocaleString()}</div>
                      </div>
                      <p className="text-sm text-white/85 whitespace-pre-wrap">{comment.body}</p>
                    </article>
                  ))}
                  {commentsHasNext ? (
                    <button
                      type="button"
                      onClick={() => loadComments(commentsPage + 1, true)}
                      disabled={commentsLoading}
                      className="rounded-lg border border-white/15 bg-white/5 px-4 py-2 text-sm font-semibold text-white/90 hover:bg-white/10 disabled:opacity-60"
                    >
                      {commentsLoading ? 'Loading...' : 'Load more'}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </section>

          </>
        ) : null}
      </main>

      {data ? (
        <PeopleActionRail
          publicId={String(data.id)}
          avatarUrl={data.logo_image_url || undefined}
          counts={{
            likes: railLikesCount,
            saves: railSavesCount,
            shares: railSharesCount,
            comments: railCommentsCount,
          }}
          myState={{ liked: railLiked, saved: railSaved }}
          shareUrl={window.location.href}
          onOpenComments={onOpenCommentsFromRail}
          onGift={onGiftFromRail}
          onToggleLike={onRailLike}
          onToggleSave={onRailSave}
          onShare={onRailShare}
        />
      ) : null}

      {data ? (
        <GiftDrawer
          open={giftOpen}
          onClose={() => setGiftOpen(false)}
          creatorName={data.name}
          avatarUrl={data.logo_image_url || undefined}
          catalog={giftCatalog}
          onSend={onSendGiftFromDrawer}
        />
      ) : null}

      <GiftToast
        visible={toast.visible}
        emoji={toast.emoji}
        label={toast.label}
        detail={toast.detail}
        kind={toast.kind}
        onHide={() => setToast((t) => ({ ...t, visible: false }))}
      />

      {bookingOpen && bookingSelected ? (
        <div className="fixed inset-0 z-[80] grid place-items-center bg-black/70 p-4">
          <div className="w-full max-w-lg rounded-2xl border border-white/15 bg-[#0b0b10] p-5">
            <div className="mb-3 text-lg font-bold">Book appointment</div>
            <div className="mb-3 text-sm text-white/70">
              {new Date(bookingSelected.starts_at).toLocaleString()} - {new Date(bookingSelected.ends_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
            </div>
            {bookingQuote ? (
              <div className="mb-3 rounded-lg border border-white/10 bg-white/5 p-3 text-xs text-white/75">
                <div>Duration: {bookingQuote.duration_minutes} min</div>
                <div>Service price: {bookingQuote.price_cents == null ? 'N/A' : `$${(bookingQuote.price_cents / 100).toFixed(2)}`}</div>
                <div>Deposit due now: ${((bookingQuote.deposit_amount_cents || 0) / 100).toFixed(2)}</div>
                <div>Platform fee (10%): ${((bookingQuote.platform_fee_cents || 0) / 100).toFixed(2)}</div>
              </div>
            ) : null}
            {bookingClientSecret ? (
              stripePromise ? (
                <>
                  <div className="mb-3 text-xs text-amber-200">
                    Complete payment within {String(Math.floor(bookingPaymentRemainingSeconds / 60)).padStart(2, '0')}:{String(bookingPaymentRemainingSeconds % 60).padStart(2, '0')}
                  </div>
                  {bookingPaymentRemainingSeconds > 0 ? (
                    <Elements stripe={stripePromise} options={{ clientSecret: bookingClientSecret, appearance: { theme: 'night' as const } }}>
                      <BookingPaymentConfirm
                        busy={bookingPaymentSubmitting}
                        setBusy={setBookingPaymentSubmitting}
                        onError={(message) => setBookingError(message || null)}
                        onSuccess={() => {
                          void onBookingPaymentSuccess()
                        }}
                      />
                    </Elements>
                  ) : (
                    <div className="text-sm text-amber-200">Payment session expired — please pick a time again.</div>
                  )}
                </>
              ) : (
                <div className="text-sm text-red-300">Stripe is not configured in this environment.</div>
              )
            ) : (
              <div className="space-y-2">
                <input
                  value={bookingName}
                  onChange={(e) => setBookingName(e.target.value)}
                  placeholder="Your name"
                  className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                />
                <input
                  value={bookingEmail}
                  onChange={(e) => setBookingEmail(e.target.value)}
                  placeholder="Email"
                  className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                />
                <input
                  value={bookingPhone}
                  onChange={(e) => setBookingPhone(e.target.value)}
                  placeholder="Phone (optional)"
                  className="w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                />
                <textarea
                  value={bookingNotes}
                  onChange={(e) => setBookingNotes(e.target.value)}
                  placeholder="Notes (optional)"
                  className="min-h-[80px] w-full rounded-lg border border-white/10 bg-black/30 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-brand"
                />
              </div>
            )}
            {bookingError ? <div className="mt-2 text-sm text-red-300">{bookingError}</div> : null}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setBookingOpen(false)
                  setBookingClientSecret('')
                  setBookingPaymentExpiresAt(null)
                }}
                className="rounded-lg border border-white/15 bg-white/5 px-4 py-2 text-sm hover:bg-white/10"
              >
                Cancel
              </button>
              {!bookingClientSecret ? (
                <button
                  type="button"
                  onClick={onCreateBooking}
                  disabled={bookingSubmitting}
                  className="rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-60"
                >
                  {bookingSubmitting ? 'Booking...' : 'Confirm'}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
